'use client';
import { useAlert } from '@/lib/alerts/hooks/useAlert';
import { AUTH_ROUTE, BLOG_ROUTE, DEMO_ROUTE, LOGIN_ROUTE, PRICING_ROUTE } from '@/lib/constants/routes';
import ax, { getToken, setToken } from '@/machinery/ax';
import { isAxiosError } from 'axios';
import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { User } from '../../models/user';
import { useNavigate } from '@tanstack/react-router';
import { useGet } from '@/machinery/useGet';
import { QueryObserverResult, RefetchOptions, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { isProduction } from '@/lib/utils/envUtils';

export interface AccessTokenResponse {
    access_token: string;
}
export interface LoginProps {
    email: string;
    password: string;
}
export interface SignupProps {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
}

type FeaturesType = {
    hasMissDirection: boolean;
};

interface AuthContextType {
    login: (data: LoginProps) => void;
    signup: (data: SignupProps) => void;
    logout: () => void;
    user?: User;
    refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
    set: (token?: string) => void;
    unset: () => void;
    mustConfirmEmail: boolean;
    userLoading: boolean;
    userLoaded: boolean;
    hasActivePlan: boolean;
    features: FeaturesType;
    loginLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const NON_REDIRECT_ROUTES = [
    AUTH_ROUTE,
    DEMO_ROUTE,
    BLOG_ROUTE,
    PRICING_ROUTE,
    'checkout-success',
    'ambassador-program',
];

function setUrl(newUrl: string) {
    window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
}

function usePrevious<T>(value: T | null) {
    const ref = useRef<T | null>(null);
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

function AuthProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const { showAlert } = useAlert();
    const [mustConfirmEmail, setMustConfirmEmail] = useState(false);

    const [accessToken, setAccessToken] = useState<string | undefined>(undefined);

    const set = (token?: string) => {
        setToken(token);
        setAccessToken(token);
    };

    const unset = useCallback(() => {
        setToken();
        setAccessToken(undefined);
    }, []);

    const redirectToLogin = useCallback(() => {
        const pathname = window.location.pathname;
        if (pathname !== '/' && !NON_REDIRECT_ROUTES.some(route => pathname && pathname.includes(route))) {
            setUrl(LOGIN_ROUTE);
        }
    }, []);

    const [startupRan, setStartupRan] = useState(false);

    const startup = useCallback(async () => {
        try {
            const token = getToken();
            if (token) {
                try {
                    const response = await ax.get<AccessTokenResponse>('/auth/token/refresh');
                    set(response?.data?.access_token);
                } catch (err) {
                    if (isAxiosError(err)) {
                        unset();
                        redirectToLogin();
                    }
                }
            } else {
                redirectToLogin();
            }
        } finally {
            setStartupRan(true);
        }
    }, [redirectToLogin, unset]);

    useEffect(() => {
        startup();
    }, [startup]);

    const {
        data: user,
        refetch,
        isLoading,
        isFetching,
        isError,
    } = useGet<User>({
        request: { url: '/auth/user' },
        enabled: !!accessToken,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (!isProduction()) return;
        if (user) {
            Sentry.setUser({
                email: user.email,
                id: user.id.toString(),
                username: user.full_name,
            });
        } else {
            Sentry.setUser(null);
        }
    }, [user]);

    const [loginLoading, setLoginLoading] = useState(false);

    const login = async ({ email, password }: LoginProps) => {
        try {
            setLoginLoading(true);
            const response = await ax.post<AccessTokenResponse>('/auth/log-in', {
                email,
                password,
            });

            set(response?.data.access_token);

            navigate({ to: '/' });
            setLoginLoading(false);
        } catch (err) {
            setLoginLoading(false);
            if (isAxiosError(err)) {
                if (err.response?.data?.detail?.name == 'NotVerified') {
                    setMustConfirmEmail(true);
                }

                showAlert({ type: 'error', text: 'Failed to login, please try again' });
            }
        } finally {
            setLoginLoading(false);
        }
    };

    const queryClient = useQueryClient();
    const logout = useCallback(async () => {
        unset();
        queryClient.clear();
        navigate({ to: '/' });
    }, [unset, queryClient, navigate]);

    useEffect(() => {
        if (isError) {
            logout();
            showAlert({ text: 'You have been logged out.', type: 'warning' });
        }
    }, [isError, logout, showAlert]);

    const signup = async ({ email, password, firstName, lastName }: SignupProps) => {
        try {
            const response = await ax.post<AccessTokenResponse>('/auth/sign-up', {
                email,
                password,
                first_name: firstName ?? '',
                last_name: lastName ?? '',
            });
            set(response?.data.access_token);

            navigate({ to: '/' });
        } catch {
            showAlert({ type: 'error', text: 'Failed to sign up, please try again' });
        }
    };

    const userLoading = useMemo(() => isLoading || isFetching, [isLoading, isFetching]);

    const [attemptedLoading, setAttemptedLoading] = useState(false);
    const loadingPrevious = usePrevious(userLoading);

    const hasActivePlan = useMemo(() => {
        return true;
        // return user?.active_team_plan?.active || user?.active_individual_plan?.active;
    }, []);

    useEffect(() => {
        if (loadingPrevious && !userLoading) {
            setAttemptedLoading(true);
        }
    }, [loadingPrevious, userLoading]);

    const features: FeaturesType = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const features = user?.features ?? {};
        return {
            hasMissDirection: true,
        };
    }, [user?.features]);

    return (
        <AuthContext.Provider
            value={{
                user,
                login,
                logout,
                signup,
                refetch,
                set,
                unset,
                mustConfirmEmail,
                userLoading,
                userLoaded: attemptedLoading || (startupRan && !accessToken),
                hasActivePlan: !!hasActivePlan,
                features,
                loginLoading,
            }}>
            {children}
        </AuthContext.Provider>
    );
}

function useAuth() {
    return useContext(AuthContext) as AuthContextType;
}

export { useAuth, AuthProvider };
